import {
  call,
  delay,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import {
  SUBSCRIBE_GRAPH,
  SUBSCRIBE_BALANCES,
  UNSUBSCRIBE_ALL_GRAPH,
  GRAPH_REALTIME_PROLONGATION,
  WS_LAYER_CHANGED,
  WS_DELETE_LAYER,
  WS_UPDATE_ACTOR_ON_LAYER,
  WS_ACTOR_BALANCE_CHANGED,
  REALTIME_EXCEPTION_LAYERS,
} from '@control-front-end/common/constants/graphRealtime';
import {
  GET_LAYER,
  UPDATE_LAYER,
} from '@control-front-end/common/constants/graphLayers';
import {
  UPDATE_ACTOR,
  GET_ACTORS_BALANCE,
  CREATE_ACTOR_LIST,
  UPDATE_ACTOR_LIST,
  WS_CREATE_ACTOR,
  WS_DELETE_ACTOR,
  WS_UPDATE_ACTOR,
  UPDATE_ACTOR_VIEW,
} from '@control-front-end/common/constants/graphActors';
import {
  RequestStatus,
  SET_MODAL,
  WS_CREATE_ACCESS,
  WS_UPDATE_ACCESS,
  WS_DELETE_ACCESS,
} from 'constants';
import { Utils } from 'mw-style-react';
import AppUtils from '@control-front-end/utils/utils';
import api from '@control-front-end/common/sagas/api';
import {
  makeGraphModels,
  updateActorOnGraph,
  sendReducerMsg,
  isActiveTemplateList,
  extendModelWithAvatars,
} from './graphHelpers';

/**
 * Отписка от изменений балансов
 */
function* subUnsubBalances(status) {
  const graphRealtimeState = yield select((state) => state.graphRealtime);
  const workspaces = yield select((state) => state.accounts);
  const nodeIds = graphRealtimeState.balances;
  if (!nodeIds.length) return;
  const { result } = yield call(api, {
    method: 'post',
    url: `/realtime/balance_subscribe/${workspaces.active}/${status}`,
    body: nodeIds,
  });
  if (result !== RequestStatus.SUCCESS) return;
  yield put({
    type: SUBSCRIBE_BALANCES.SUCCESS,
    payload: status === 'on' ? nodeIds : [],
  });
}

/**
 * Подписка\отписка от всех уведомлений
 */
function* subUnsub(status) {
  const graphRealtimeState = yield select((state) => state.graphRealtime);
  for (const sub of graphRealtimeState.layers) {
    yield put({ type: SUBSCRIBE_GRAPH.REQUEST, payload: { ...sub, status } });
  }
  yield subUnsubBalances(status);
}

/**
 * Сохранить изменения слоя
 */
function* saveLayerChanges({ copyList, layer, layerIndex, data }) {
  const { nodes, edges } = yield call(makeGraphModels, {
    nodes: data.data.nodes,
    edges: data.data.edges,
    replaceAll: true,
  });
  const newLayerModel = {
    ...layer,
    nodes,
    edges,
  };
  copyList.splice(layerIndex, 1, newLayerModel);
  yield put({
    type: GET_LAYER.SUCCESS,
    payload: { list: copyList },
  });
}

/**
 * Получить объект для подписки
 */
function getSubObj({ layerId, typeLayer }) {
  let obj;
  let id;
  switch (typeLayer) {
    case 'layers':
      obj = 'layer';
      id = layerId;
      break;
    case 'actors':
      obj = 'actor';
      id = layerId;
      break;
    case 'trees':
      obj = 'accountTree';
      id = layerId.split('_')[1];
      break;
    default:
  }
  return { id, obj };
}

/**
 * Получить слой по id
 */
function* getLayer(layerId) {
  const graphL = yield select((state) => state.graphLayers);
  const copyList = graphL.list.slice();
  const layerIndex = copyList.findIndex((i) => i.id === layerId);
  if (layerIndex === -1) return false;
  return {
    copyList,
    layer: copyList[layerIndex],
    layerIndex,
    isActive: graphL.active === layerId,
  };
}

/**
 * Получить слои с layerId
 */
function* getLayers(layerId) {
  const graphL = yield select((state) => state.graphLayers);
  const copyList = graphL.list.slice();
  const layers = copyList.filter((i) => i.layerId === layerId);
  if (!layers.length) return false;
  const isActive = layers.find((i) => i.id === graphL.active);
  return { copyList, layers, isActive };
}

/**
 * Обновить измененный неактивный слой
 */
function* updateInactiveLayer({ layer, layerIndex, copyList }) {
  const layerCopy = { ...layer };
  delete layerCopy.nodes;
  delete layerCopy.edges;
  copyList.splice(layerIndex, 1, layerCopy);
  yield put({ type: UPDATE_LAYER.SUCCESS, payload: { list: copyList } });
  const subObj = getSubObj({ layerId: layer.id, typeLayer: layer.typeLayer });
  yield put({
    type: SUBSCRIBE_GRAPH.REQUEST,
    payload: { ...subObj, status: 'off' },
  });
}

/**
 * Актуализировать балансы
 */
function mergeExistBalances({ layer, data }) {
  const actors = layer.nodes.filter((i) => i.data.type === 'node');
  const newActorIds = [];
  data.data.nodes.forEach((node) => {
    const fNode = actors.find((i) => i.data.actorId === node.id);
    if (fNode && fNode.data) {
      const { balance, balanceVector } = fNode.data;
      node.balance = balance;
      node.balanceVector = balanceVector;
    } else {
      newActorIds.push(node.id);
    }
  });
  const actorIds = actors.map((i) => i.data.actorId).concat(newActorIds);
  return { actorIds, newActorIds };
}

/**
 * Получить новые балансы акторов
 */
export function* getNewBalances({ layerId, actorIds }) {
  if (!actorIds.length) return;
  const { balances } = yield select((state) => state.graphRealtime);
  let balanceFilter;
  if (balances.length) {
    balanceFilter = balances[0];
  } else {
    const filtersLS = Utils.fromStorage('actors_layer_filters');
    balanceFilter = filtersLS ? JSON.parse(filtersLS) : {};
  }
  const { nameId, currencyId } = balanceFilter;
  yield put({
    type: GET_ACTORS_BALANCE.REQUEST,
    payload: {
      layerId,
      actorIds,
      currencyId: currencyId !== 'none' ? currencyId : null,
      nameId: nameId !== 'none' ? nameId : null,
    },
  });
}

/**
 * Обновить подписку на балансы по слою
 */
export function* resubscribeBalances({ layerId }) {
  const { balances } = yield select((state) => state.graphRealtime);
  if (!balances.length) return;
  const { nameId, currencyId } = balances[0];
  yield put({
    type: SUBSCRIBE_BALANCES.REQUEST,
    payload: { layerId, nameId, currencyId, status: 'on' },
  });
}

/**
 * Изменения для слоя счета
 */
function* accountTreeChanges({ edgeTypeId }) {
  const graphL = yield select((state) => state.graphLayers);
  const copyList = graphL.list.slice();
  const trees = AppUtils.findAllIndexes(copyList, (i) =>
    i.id.includes(`_${edgeTypeId}`)
  );
  for (const layerIndex of trees) {
    const layer = copyList[layerIndex];
    if (layer.id !== graphL.active) {
      delete layer.nodes;
      delete layer.edges;
      copyList.splice(layerIndex, 1, layer);
    } else {
      const edgeTypes = yield select((state) => state.edgeTypes);
      const edgeTypeH = edgeTypes.find((i) => i.id === edgeTypeId);
      if (!edgeTypeH) return;
      const actorId = graphL.active.split('_')[0];
      const { result, data } = yield call(api, {
        method: 'get',
        url: `/graph/tree/${actorId}`,
        queryParams: { edgeTypeId: edgeTypeH.id },
      });
      if (result !== RequestStatus.SUCCESS) return;
      const { actorIds } = yield mergeExistBalances({ layer, data });
      yield saveLayerChanges({ copyList, layer, layerIndex, data });
      // запрашиваем балансы всех акторов, т.к. они считаются наверх по дереву
      yield getNewBalances({ layerId: graphL.active, actorIds });
      yield resubscribeBalances({ layerId: graphL.active });
    }
  }
  yield put({ type: UPDATE_LAYER.SUCCESS, payload: { list: copyList } });
}

/**
 * Изменения для обычного слоя
 */
function* layerChanges({ layerId }) {
  const res = yield getLayers(layerId);
  if (!res) return;
  const { copyList, layers, isActive } = res;
  if (!isActive) {
    for (const item of layers) {
      const layerIndex = copyList.findIndex((i) => i.id === item.id);
      yield updateInactiveLayer({ layer: item, layerIndex, copyList });
    }
    return;
  }
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/graph_layers/${layerId}`,
    queryParams: { noDuplicate: 'false' },
  });
  if (result !== RequestStatus.SUCCESS) return;
  for (const item of layers) {
    if (!item.nodes) return;
    const { newActorIds } = yield mergeExistBalances({ layer: item, data });
    const layerIndex = copyList.findIndex((i) => i.id === item.id);
    yield saveLayerChanges({ copyList, layer: item, layerIndex, data });
    yield getNewBalances({ layerId, actorIds: newActorIds });
    yield resubscribeBalances({ layerId });
  }
}

/**
 * Изменения для слоя актора
 */
function* actorChanges({ actorId }) {
  const res = yield getLayer(actorId);
  if (!res) return;
  const { copyList, layer, layerIndex, isActive } = res;
  if (!isActive) {
    yield updateInactiveLayer({ layer, layerIndex, copyList });
    return;
  }
  const edgeTypes = yield select((state) => state.edgeTypes);
  const edgeTypeH = edgeTypes.find((i) => i.name === 'hierarchy');
  if (!edgeTypeH) return;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/graph/linked_actors/${actorId}`,
    queryParams: { edgeTypeId: edgeTypeH.id },
  });
  if (result !== RequestStatus.SUCCESS) return;
  const { newActorIds } = yield mergeExistBalances({ layer, data });
  yield saveLayerChanges({ copyList, layer, layerIndex, data });
  yield getNewBalances({ layerId: actorId, actorIds: newActorIds });
  yield resubscribeBalances({ layerId: actorId });
}

// =============================================================================

/**
 * Применить изменения графа
 */
function* layerChanged({ payload }) {
  const { layerId: id, obj } = payload;
  switch (obj) {
    case 'layer':
      yield layerChanges({ layerId: id });
      break;
    case 'actor':
      yield actorChanges({ actorId: id });
      break;
    case 'accountTree':
      yield accountTreeChanges({ edgeTypeId: id });
      break;
    default:
  }
}

/**
 * Применить изменение актора на слое
 */
function* updateActorOnLayer({ payload }) {
  const graphL = yield select((state) => state.graphLayers);
  const layersWithActor = graphL.list.filter(
    (i) => i.nodes && i.nodes.find((node) => node.data.actorId === payload.id)
  );
  if (!layersWithActor.length) return;
  const newActor = payload;
  yield call(extendModelWithAvatars, newActor);
  for (const layer of layersWithActor) {
    const { nodes } = yield call(updateActorOnGraph, {
      actorModel: newActor,
      layerId: layer.id,
    });
    yield sendReducerMsg({
      layerId: layer.id,
      type: UPDATE_ACTOR.SUCCESS,
      payload: { nodes, editableElement: null },
    });
  }
}

/**
 * Закрыть удаленный слой
 */
function* layerDeleted({ payload }) {
  const { id: layerId } = payload;
  const graphL = yield select((state) => state.graphLayers);
  const layer = graphL.list.find((i) => i.id === layerId);
  if (!layer) return;
  yield put({
    type: SET_MODAL,
    payload: {
      name: 'LayerDeleted',
      data: { layerId, layerName: layer.name },
    },
  });
}

/**
 * Применить изменения балансы акторов
 */
function* balancesChanged({ payload }) {
  const { nameId, currencyId, actorId } = payload;
  const graphL = yield select((state) => state.graphLayers);
  const layer = graphL.list.find((i) => i.id === graphL.active);
  if (!layer) return;
  const changedActor = layer.nodes.find(
    (i) => i.data.actorId === payload.actorId
  );
  if (!changedActor) return;
  yield put({
    type: GET_ACTORS_BALANCE.REQUEST,
    payload: {
      layerId: graphL.active,
      nameId,
      currencyId,
      actorIds: [actorId],
    },
  });
}

/**
 * По интервалу подтверждаем подписку на слой
 */
function* runSubProlongation() {
  const INTERVAL = 60000;
  yield delay(INTERVAL);
  yield subUnsub('on');
  yield put({ type: GRAPH_REALTIME_PROLONGATION });
}

/**
 * Отписаться от всех подписок
 */
function* unsubscribeAllGraph() {
  yield subUnsub('off');
  yield put({ type: UNSUBSCRIBE_ALL_GRAPH.SUCCESS, payload: {} });
}

/**
 * Подписка\отписка на realtime изменения на слое
 */
function* subscribeGraph({ payload }) {
  const { id, obj, status } = payload;
  const workspaces = yield select((state) => state.accounts);
  if (obj === 'layer') {
    const graphL = yield select((state) => state.graphLayers);
    const layer = graphL.list.find((i) => i.id === id);
    if (
      layer &&
      layer.isSystem &&
      REALTIME_EXCEPTION_LAYERS.includes(layer.name)
    )
      return;
  }
  const { result } = yield call(api, {
    method: 'post',
    url: `/realtime/graph_subscribe/${workspaces.active}/${status}`,
    body: { id, obj },
  });
  if (result !== RequestStatus.SUCCESS) return;
  const graphRealtimeState = yield select((state) => state.graphRealtime);
  let layerList;
  if (status === 'on') {
    const copyLayers = graphRealtimeState.layers.slice();
    const fEl = copyLayers.find((i) => i.id === id && i.obj === obj);
    if (!fEl) copyLayers.push({ id, obj });
    layerList = copyLayers;
  } else {
    layerList = graphRealtimeState.layers.filter(
      (i) => i.id !== id && i.obj !== obj
    );
  }
  yield put({ type: SUBSCRIBE_GRAPH.SUCCESS, payload: layerList });
}

/**
 * Подписка\отписка на realtime изменения балансов акторов
 */
function* subscribeBalances({ payload }) {
  const { nameId, currencyId, layerId, status } = payload;
  const workspaces = yield select((state) => state.accounts);
  const graphL = yield select((state) => state.graphLayers);
  const layer = graphL.list.find(
    (i) => i.id === layerId || i.layerId === layerId
  );
  if (!layer) return;
  yield subUnsubBalances('off');
  const isException =
    layer.isSystem && REALTIME_EXCEPTION_LAYERS.includes(layer.name);
  if (!nameId || !currencyId || isException) return;
  const copyNodes = layer.nodes ? layer.nodes.slice() : [];
  const nodeIds = copyNodes
    .filter((i) => i.data.type === 'node')
    .map((a) => ({ actorId: a.data.actorId, nameId, currencyId }));
  if (nodeIds.length) {
    const { result } = yield call(api, {
      method: 'post',
      url: `/realtime/balance_subscribe/${workspaces.active}/${status}`,
      body: nodeIds,
    });
    if (result !== RequestStatus.SUCCESS) return;
  }
  yield put({ type: SUBSCRIBE_BALANCES.SUCCESS, payload: nodeIds });
}

/**
 * Проверить надо ли обрабатывать пакет из WS
 */
function* isHandleWSPacket(payload) {
  const accounts = yield select((state) => state.accounts);
  const { accId } = payload.model;
  return accounts.active === accId;
}

/**
 * Добавить новый актор в список
 */
function* wsAddActor({ payload }) {
  const isHandle = yield call(isHandleWSPacket, payload);
  if (!isHandle) return;
  const isActive = yield isActiveTemplateList(payload.model);
  if (!isActive) return;
  const actorsList = yield select((state) => state.actorsList);
  const { model } = payload;
  const newList = actorsList.list.slice();
  const findEl = newList.find((i) => i.id === model.id);
  if (findEl) return;
  yield call(extendModelWithAvatars, model);
  model.isNew = true;
  newList.unshift(model);
  yield put({
    type: CREATE_ACTOR_LIST.SUCCESS,
    payload: { list: newList },
  });
}

/**
 * Удалить актор из списка
 */
function* wsRemoveActor({ payload }) {
  const isHandle = yield call(isHandleWSPacket, payload);
  if (!isHandle) return;
  const isActive = yield isActiveTemplateList(payload.model);
  const { model, userId } = payload;
  if (isActive) {
    const actorsList = yield select((state) => state.actorsList);
    const newList = actorsList.list.filter((i) => i.id !== model.id);
    yield put({
      type: CREATE_ACTOR_LIST.SUCCESS,
      payload: { list: newList },
    });
  }
  const actorView = yield select((state) => state.actorView);
  const auth = yield select((state) => state.auth);
  if (actorView.id === model.id && auth.id !== userId) {
    const mergeActor = { ...actorView, status: 'removed' };
    yield put({
      type: UPDATE_ACTOR_VIEW.SUCCESS,
      payload: mergeActor,
    });
  }
}

/**
 * Обновить актор в списке
 */
function* wsUpdateActor({ payload }) {
  const isHandle = yield call(isHandleWSPacket, payload);
  if (!isHandle) return;
  const isActive = yield isActiveTemplateList(payload.model);
  const { model } = payload;
  yield call(extendModelWithAvatars, model);
  if (isActive) {
    const actorsList = yield select((state) => state.actorsList);
    const actor = actorsList.list.find((i) => i.id === model.id);
    model.isNew = actor ? actor.isNew : model.isNew;
    const newList = AppUtils.replaceItemArrayObject(
      actorsList.list,
      'id',
      model
    );
    yield put({
      type: actor ? UPDATE_ACTOR_LIST.SUCCESS : CREATE_ACTOR_LIST.SUCCESS,
      payload: { list: newList },
    });
  }
  const actorView = yield select((state) => state.actorView);
  if (actorView.id === model.id) {
    const mergeActor = { ...actorView, ...model };
    yield put({
      type: UPDATE_ACTOR_VIEW.SUCCESS,
      payload: mergeActor,
    });
  }
}

/**
 * Добавлен доступ на актор
 */
function* wsCreateActorAccess({ payload }) {
  const isHandle = yield call(isHandleWSPacket, payload);
  if (!isHandle) return;
  if (payload.objType !== 'actor') return;
  yield wsAddActor({ payload });
}

/**
 * Обновлен доступ на актор
 */
function* wsUpdateActorAccess({ payload }) {
  const isHandle = yield call(isHandleWSPacket, payload);
  if (!isHandle) return;
  if (payload.objType !== 'actor') return;
  yield wsUpdateActor({ payload });
}

/**
 * Удален доступ на актор
 */
function* wsRemoveActorAccess({ payload }) {
  if (payload.objType !== 'actor') return;
  const auth = yield select((state) => state.auth);
  const access = payload.model.access;
  const findMe = access.find((i) => i.userId === auth.id);
  if (findMe) {
    yield wsUpdateActor({ payload });
  } else {
    yield wsRemoveActor({ payload });
  }
}

function* graphRealtime() {
  yield takeEvery(SUBSCRIBE_GRAPH.REQUEST, subscribeGraph);
  yield takeEvery(SUBSCRIBE_BALANCES.REQUEST, subscribeBalances);
  yield takeEvery(UNSUBSCRIBE_ALL_GRAPH.REQUEST, unsubscribeAllGraph);
  yield takeEvery(GRAPH_REALTIME_PROLONGATION, runSubProlongation);
  yield takeLatest(WS_LAYER_CHANGED, layerChanged);
  yield takeLatest(WS_DELETE_LAYER, layerDeleted);
  yield takeLatest(WS_ACTOR_BALANCE_CHANGED, balancesChanged);
  yield takeLatest(WS_UPDATE_ACTOR_ON_LAYER, updateActorOnLayer);
  yield takeLatest(WS_CREATE_ACTOR, wsAddActor);
  yield takeLatest(WS_DELETE_ACTOR, wsRemoveActor);
  yield takeLatest(WS_UPDATE_ACTOR, wsUpdateActor);
  yield takeLatest(WS_CREATE_ACCESS, wsCreateActorAccess);
  yield takeLatest(WS_UPDATE_ACCESS, wsUpdateActorAccess);
  yield takeLatest(WS_DELETE_ACCESS, wsRemoveActorAccess);
}

export default graphRealtime;
